import { PencilIcon } from '@heroicons/react/outline'
import { InformationCircleIcon } from '@heroicons/react/solid'
import classNames from 'classnames'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import CrmApi from 'shared/api/crm/Api'
import { FindLastSystemUserTalkedToCustomerResponse } from 'shared/api/crm/ApiGlobals'
import BaseInput from 'shared/components/base/form/base-input'
import BaseSelect, { OptionType } from 'shared/components/base/form/base-select'
import Image from 'shared/components/base/image/Image'
import CustomTooltip1 from 'shared/components/base/tooltip/custom-tooltip1'
import FetchData from 'shared/components/FetchData'
import { BASE_CRM_URL } from 'shared/constants/defaultENV'
import dictionary from 'shared/constants/dictionary'
import { useEmit } from 'shared/contexts/socket/socket-context'
import {
  setLeadName,
  setPreLeadName,
} from 'store/slice/call-detail/call-detail.slice'
import { CallDataType } from 'store/slice/call-detail/types'
import { RootState } from 'store/store'

type PropType = {
  name: string
  isLead: boolean
  id?: string
  etc?: number
  preName?: CallDataType['preName']
}

const CallerContactName = (props: PropType) => {
  const { name, id, etc, isLead } = props
  const emit = useEmit()
  const callDetail = useSelector((state: RootState) => state.callDetail)
  const { callData } = callDetail
  const [isEditingMode, setIsEditingMode] = useState(false)
  const [preNameOptions, setPreNameOptions] = useState<OptionType<number>[]>([])
  const href = `${BASE_CRM_URL}?etc=${etc}&pagetype=entityrecord&id=${id}`
  const dispatch = useDispatch()
  const { formData } = useSelector((state: RootState) => state.callDetail)

  const fetchLastExpertTalked = () => {
    return CrmApi.systemUser
      .systemUserFindLastSystemUserTalkedToCustomer({
        phoneNumber: callData?.source || '',
      })
      .then((res) => res.data)
  }

  useEffect(() => {
    emit('getLeadPreNames')
      .then((options?: { label: string; value: number }[]) => {
        if (!options) {
          toast.error(dictionary.socket_timeout)
          return
        }

        options.sort((a, b) => {
          if (a.label === 'حاج آقا') return 1
          if (b.label === 'حاج آقا') return -1
          return a.label <= b.label ? -1 : 1
        })
        const _options = options.map((item) => ({
          ...item,
          key: item.value,
        }))
        setPreNameOptions(_options)
      })
      .catch(() => {
        toast.error(dictionary.socket_timeout)
      })
  }, [])

  const preNameLabel = useMemo(
    () =>
      preNameOptions.find((item) => item.value === formData.preLeadName)?.label,
    [formData.preLeadName, preNameOptions]
  )

  return (
    <>
      {isEditingMode ? (
        <div className={'flex mb-3'}>
          <div className="flex items-center pl-2">
            <BaseSelect<number>
              options={preNameOptions as any}
              value={formData.preLeadName || 0}
              className={'w-[4.9rem] rounded-none rounded-l-none '}
              setInputClassName={() => classNames(' rounded-l-none !h-10')}
              onSelect={(value) => dispatch(setPreLeadName(value))}
            />
            <BaseInput
              inputClassName={
                '!border-r-none min-h-[2.5625rem] border-r-0 rounded-r-none !h-10 bg-white'
              }
              value={formData.leadName}
              onChange={(e) => dispatch(setLeadName(e.target.value))}
            />
          </div>
        </div>
      ) : null}
      <div className={'flex items-center gap-x-0.5 mb-3 mr-5'}>
        نام :
        <span>
          <a
            href={href}
            className={'focus-visible:outline-0'}
            onClick={(e) => {
              e.preventDefault()
              if (id && etc) {
                window.open(
                  href,
                  'Snopzer',
                  'left=20,top=20,width=1360,height=768,toolbar=1,resizable=0'
                )
              }
            }}
          >
            {preNameLabel} {name}
          </a>
        </span>
        {!callData.isNewCustomer && callData.leadName && !isEditingMode && (
          <>
            <PencilIcon
              className={
                'w-3.5 h-3.5 text-[#939597] mr-2 mb-1 text-base cursor-pointer'
              }
              onClick={() => setIsEditingMode(true)}
            />
            {isLead ? (
              <span
                className={
                  'text-[#107715] bg-[#ECFDE9] rounded px-3 py-1 text-sm font-semibold'
                }
              >
                lead
              </span>
            ) : null}
          </>
        )}
        <FetchData
          request={fetchLastExpertTalked}
          deps={[callData?.source]}
          handleLoading={false}
          handleEmptyData={false}
          handleError={false}
        >
          {(
            lastExpertToCustomer: FindLastSystemUserTalkedToCustomerResponse
          ) => {
            if (!lastExpertToCustomer) return
            return (
              <CustomTooltip1
                divider={false}
                icon
                title="آخرین فردی که با مشتری صحبت کرده"
                className="mr-20"
                subTitle={
                  <div className="flex items-center mb-1 gap-x-2">
                    <Image
                      className="w-[30px] h-[30px] rounded-full "
                      src={lastExpertToCustomer?.imageUrl || ''}
                      alt="avatar"
                    />
                    <div className="font-medium text-[13px] leading-[22px]">
                      {lastExpertToCustomer?.fullname}
                    </div>
                  </div>
                }
              >
                <InformationCircleIcon className="w-4 h-4 text-[#109CF1]" />
              </CustomTooltip1>
            )
          }}
        </FetchData>
      </div>
    </>
  )
}

export default CallerContactName
